"use client";

import type { ReactNode } from "react";
import { AnimatePresence } from "motion/react";

import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/lib/format";
import type { RenderedRunEventBlock } from "@/lib/task-run-ui";
import { cn } from "@/lib/utils";

import { TaskEventTranscriptRow } from "./task-event-transcript-row";

/** What a plugin or tool step's badge is drawn from. */
export type EventPluginBadgeInput = {
  slug?: string;
  toolName?: string;
  label: string;
  kind: "tool" | "plugin";
  variant: "event";
  className: string;
};

function readPayloadString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/** Inline description body; badges worker/model ids where we have structured payload. */
export function statusDescriptionContent(block: RenderedRunEventBlock): ReactNode {
  if (block.type === "assistant-stream") {
    return block.description;
  }
  const workerBadgeClass =
    "ml-0.5 inline-flex max-w-[min(100%,16rem)] min-w-0 shrink truncate align-baseline";
  if (block.kind === "run.claimed") {
    const workerId = readPayloadString(block.payload.workerId);
    if (workerId) {
      return (
        <>
          {`Claimed by `}
          <Badge variant="worker" className={workerBadgeClass}>
            {workerId}
          </Badge>
          .
        </>
      );
    }
  }
  if (block.kind === "run.reclaimed") {
    const workerId = readPayloadString(block.payload.workerId);
    if (workerId) {
      return (
        <>
          {`Lease expired and `}
          <Badge variant="worker" className={workerBadgeClass}>
            {workerId}
          </Badge>
          {` reclaimed the run.`}
        </>
      );
    }
  }
  if (block.kind === "worker.started") {
    const runtimeMode = readPayloadString(block.payload.runtimeMode);
    if (runtimeMode) {
      return (
        <>
          {`Worker entered `}
          <Badge
            variant="live"
            className="mx-0.5 inline-flex max-w-[min(100%,12rem)] min-w-0 shrink truncate align-baseline"
          >
            {runtimeMode}
          </Badge>
          {` mode.`}
        </>
      );
    }
  }
  if (block.kind === "runtime.started") {
    const provider = readPayloadString(block.payload.provider);
    const model = readPayloadString(block.payload.model);
    if (provider && model) {
      return (
        <>
          {`Using ${provider} / `}
          <Badge
            variant="model"
            className="ml-0.5 inline-flex max-w-[min(100%,14rem)] min-w-0 shrink truncate align-baseline"
          >
            {model}
          </Badge>
          .
        </>
      );
    }
  }
  return block.description;
}

/**
 * A trace line's title: queued and completed as status badges, a plugin or
 * tool step as its plugin's badge (through `pluginBadge`), anything else as
 * an event badge.
 */
export function EventBlockTitleView({
  block,
  pluginBadge,
}: {
  block: RenderedRunEventBlock;
  pluginBadge: (input: EventPluginBadgeInput) => ReactNode;
}) {
  if (block.type === "status" && block.kind === "run.queued") {
    return (
      <Badge variant="queued" className="align-baseline text-xs font-semibold">
        {block.title}
      </Badge>
    );
  }
  if (block.type === "status" && block.kind === "run.completed") {
    return (
      <Badge
        variant="completed"
        className="align-baseline text-xs font-semibold"
      >
        {block.title}
      </Badge>
    );
  }

  const pluginSlug =
    block.type === "status"
      ? readPayloadString(block.payload.pluginSlug)
      : undefined;
  const toolName =
    block.type === "status"
      ? readPayloadString(block.payload.toolName)
      : undefined;
  if (pluginSlug || toolName) {
    return pluginBadge({
      slug: pluginSlug,
      toolName,
      label: block.title,
      kind: toolName ? "tool" : "plugin",
      variant: "event",
      className: "align-baseline text-xs font-semibold",
    });
  }

  return (
    <Badge variant="event" className="align-baseline text-xs font-semibold">
      {block.title}
    </Badge>
  );
}

/**
 * A run's trace as the transcript prints it: one line per rendered event
 * block, the newest still working while the run is. TaskEventBlocks renders
 * its transcript variant through this with the live plugin catalogue and
 * each line's extra detail; the list itself reads no run data.
 */
export function TaskEventTranscriptList(props: {
  blocks: RenderedRunEventBlock[];
  isRunning?: boolean;
  className?: string;
  pluginBadge: (input: EventPluginBadgeInput) => ReactNode;
  /** Detail beneath a line, such as matched sessions or the raw payload. */
  renderDetail?: (block: RenderedRunEventBlock) => ReactNode;
}) {
  const { blocks } = props;
  return (
    <div className={cn("flex flex-col gap-2", props.className)}>
      <AnimatePresence initial={false}>
        {blocks.map((block, index) => (
          <TaskEventTranscriptRow
            key={block.id}
            working={props.isRunning && index === blocks.length - 1}
            hint={`${block.title}${block.description ? ` · ${block.description}` : ""}`}
            title={<EventBlockTitleView block={block} pluginBadge={props.pluginBadge} />}
            description={
              block.description ? statusDescriptionContent(block) : undefined
            }
            dateLabel={formatDateTime(block.createdAt)}
          >
            {props.renderDetail?.(block)}
          </TaskEventTranscriptRow>
        ))}
      </AnimatePresence>
    </div>
  );
}
